/**
 * @file
 * ---------------------------------------------------------------------------
 * RAOC-23 migration shim — captured-`siteId` store (FG-true path only)
 * ---------------------------------------------------------------------------
 * Co-located helper for the `rovo_cas_use_rovo_config` migration shim. Pairs
 * with `./rovo-connectors-config.ts`.
 *
 * Under the `rovo_cas_use_rovo_config` FG, connector data is fetched
 * **declaratively** by `useRovoConfigStore({ cloudId })` inside the
 * `*FromRovoConfig` selector hooks — not by the imperative
 * `fetchIfEmpty` / `prefetchIfEmpty` store actions (those are no-ops on this
 * path, see `./rovo-connectors-config.ts`).
 *
 * The tenant (`siteId` === cloudId), however, only enters the system through
 * those imperative action calls (`fetchIfEmpty(fetcher, siteId)` in
 * `useEnsureConnectorsLoaded`, `prefetchIfEmpty(fetcher, siteId, …)` in
 * `WarmChatFetches`). This tiny reactive store bridges the two: the action
 * stubs capture the `siteId` into it, and the selector hooks read it back via
 * `useRovoConfigSiteId()` and forward it to `useRovoConfigStore({ cloudId })`.
 *
 * It is intentionally a `react-sweet-state` store (not a module-level
 * `let`) so that capturing a `siteId` re-renders any mounted selector hook and
 * switches its `useRovoConfigStore` call from idle to active.
 *
 * IMPORTANT: this store is part of the FG-true path *only*. The legacy
 * knowledge-store path in `./index.ts` neither reads nor writes it.
 *
 * TODO RAOC-23: once `rovo_cas_use_rovo_config` is fully rolled out and the
 *               chat call sites supply a tenant directly, the indirection
 *               here can be removed in favour of threading `cloudId` straight
 *               into the selector hooks.
 * ---------------------------------------------------------------------------
 */

import {
	createActionsHook,
	createStateHook,
	createStore,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

type RovoConfigSiteIdState = {
	/** The tenant cloudId most recently captured from a fetch/prefetch action call. */
	siteId: string | undefined;
};

type RovoConfigSiteIdActions = {
	setSiteId: (
		siteId: string | undefined,
	) => ({ getState, setState }: StoreActionApi<RovoConfigSiteIdState>) => void;
};

const initialState: RovoConfigSiteIdState = {
	siteId: undefined,
};

const siteIdStore: Store<RovoConfigSiteIdState, RovoConfigSiteIdActions> = createStore({
	name: 'rovoConfigSiteIdStore',
	initialState,
	actions: {
		setSiteId:
			(
				siteId: string | undefined,
			): (({ getState, setState }: StoreActionApi<RovoConfigSiteIdState>) => void) =>
			({ getState, setState }) => {
				// Only re-render consumers when the tenant actually changes.
				if (getState().siteId === siteId) {
					return;
				}
				setState({ siteId });
			},
	},
});

/**
 * Reactive read of the captured tenant cloudId. Returns `undefined` until a
 * fetch/prefetch action has captured a `siteId`, in which case downstream
 * `useRovoConfigStore({ cloudId: undefined })` stays safely idle.
 */
export const useRovoConfigSiteId: () => string | undefined = createStateHook(siteIdStore, {
	selector: (state) => state.siteId,
});

const useSiteIdActions = createActionsHook(siteIdStore);

/**
 * Hook returning the imperative setter used by the fetch/prefetch action stubs
 * to capture the tenant cloudId into the reactive store. The returned setter is
 * stable across renders (react-sweet-state action identity), so it is safe to
 * close over inside per-render action objects.
 */
export const useCaptureRovoConfigSiteId = (): ((siteId: string | undefined) => void) => {
	const { setSiteId } = useSiteIdActions();
	return setSiteId;
};

export { siteIdStore as rovoConfigSiteIdStore };
